import { Pouch, Statistics, Group } from '../types';
import { isInMuteGroup, isMuteGroup, sheetContributingPouchesAreAllTime, pouchCountsAsTimeForSheetDisplay } from './GroupLogic';
import { getPouchEffectiveContribution } from './BalancingPouchLogic';

/**
 * パウチの実際の値（正負と乗数を考慮）を取得する
 * @param pouch パウチオブジェクト
 * @param tempValue 一時的な値（オプション）
 * @param groups グループの配列（ミュートグループ判定用）
 * @returns 実際の値
 */
export const getPouchActualValue = (pouch: Pouch, groups: Group[] = [], tempValue?: number): number => {
  // 削除中のパウチは0として扱う
  if (pouch._isDeleting) {
    return 0;
  }

  // ミュートグループに所属するパウチは0として扱う
  if (isInMuteGroup(pouch.id, groups)) {
    return 0;
  }

  // 値の決定（一時的な値があればそれを使用）
  const baseValue = tempValue !== undefined ? tempValue : pouch.value;
  let value;
  
  // 乗数が負数の場合は割り算モード
  if (pouch.multiplier < 0) {
    value = baseValue / Math.abs(pouch.multiplier);
  } else {
    // 通常の掛け算モード
    value = baseValue * pouch.multiplier;
  }
  
  return value;
};

/**
 * 帳尻ロック時の「合計 − ターゲット」差分の表示用。
 * 乗算・除算の浮動小数誤差で -2.2e-8 のような残差が出るのを抑え、実質一致なら 0 にする。
 */
export const snapBalancingTotalDiff = (actualTotal: number, targetTotal: number): number => {
  const raw = actualTotal - targetTotal;
  if (!Number.isFinite(raw)) return 0;
  const rounded = Math.round(raw * 1e6) / 1e6;
  return Math.abs(rounded) < 1e-6 ? 0 : rounded;
};

/**
 * 参照先グループのCOUNTを計算する（GTP用）
 * @param referencedGroupId 参照先グループのID
 * @param pouches すべてのパウチの配列
 * @param groups グループの配列
 * @param excludePouchId 除外するパウチのID（自分自身）
 * @returns 参照先グループのCOUNT
 */
export const calculateReferencedGroupCount = (
  referencedGroupId: string, 
  pouches: Pouch[], 
  groups: Group[] = [], 
  excludePouchId?: string
): number => {
  const referencedGroup = groups.find(g => g.id === referencedGroupId);
  if (!referencedGroup) {
    return 0;
  }

  // 参照先グループに所属するパウチをフィルタリング（自分自身を除く）
  const groupPouches = pouches.filter(p => 
    referencedGroup.pouches?.includes(p.id) && 
    !p._isDeleting && 
    !isInMuteGroup(p.id, groups) &&
    p.id !== excludePouchId
  );

  // 各パウチのCOUNTを計算して合計
  return groupPouches.reduce((total, pouch) => {
    const count = pouch.multiplier < 0 
      ? 1 / Math.abs(pouch.multiplier)  // 割り算モードの場合は1/除数
      : pouch.multiplier;               // 掛け算モードの場合は乗数
    return total + count;
  }, 0);
};

/**
 * シート内のパウチから統計情報を計算する
 * @param pouches パウチの配列
 * @param groups グループの配列
 * @returns 統計情報オブジェクト
 */
export const calculateStatistics = (pouches: Pouch[], groups: Group[] = []) => {
  // ミュートグループに所属しないパウチのみをフィルタリング
  const validPouches = pouches.filter(pouch => 
    !pouch._isDeleting && !isInMuteGroup(pouch.id, groups)
  );

  // 有効なパウチがない場合は初期値を返す
  if (validPouches.length === 0) {
    return {
      count: 0,
      total: 0,
      average: 0,
      max: 0,
      min: 0
    };
  }

  // 各パウチの実際の値を計算（乗数を考慮）
  const values = validPouches.map(pouch => {
    // パウチの値をそのまま使用（valueは既に正負の符号を含んでいる）
    const baseValue = pouch.value;
    // 乗数適用後の値（シート合計・グループ小計と同一の定義。時間は常に value×multiplier）
    const effectiveValue = getPouchEffectiveContribution(pouch);
    
    // 時間パウチ・時間グループ参照のGTPは actualValue（分数）に effectiveValue を使う
    const countsAsTime = pouchCountsAsTimeForSheetDisplay(pouch, pouches, groups);
    const actualValue = countsAsTime ? effectiveValue : baseValue;
    
    // GTPの場合は参照先グループのCOUNT × GTPの純マルチで計算
    let count: number;
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      const referencedGroupCount = calculateReferencedGroupCount(
        pouch.referencedGroupId, 
        pouches, 
        groups, 
        pouch.id
      );
      const gtpMultiplier = pouch.multiplier < 0 
        ? 1 / Math.abs(pouch.multiplier)  // 割り算モードの場合は1/除数
        : pouch.multiplier;               // 掛け算モードの場合は乗数
      count = referencedGroupCount * gtpMultiplier;
    } else {
      // 通常のパウチの場合は従来通り
      count = pouch.multiplier < 0 
        ? 1 / Math.abs(pouch.multiplier)  // 割り算モードの場合は1/除数
        : pouch.multiplier;               // 掛け算モードの場合は乗数
    }
    
    return {
      baseValue,      // 乗数適用前の値（掛け算モードのMAX/MIN用）
      effectiveValue, // 乗数適用後の値（合計用）
      actualValue,    // 時間パウチの場合はactualValue（分数）
      isDivision: pouch.multiplier < 0,
      isTime: countsAsTime,
      count: count,
      averageCount: pouch.multiplier < 0 
        ? 1 / Math.abs(pouch.multiplier)  // 割り算モードの場合は1/除数（重みベース）
        : pouch.multiplier               // 掛け算モードの場合は乗数
    };
  });

  // 統計情報を計算
  const total = Math.round(values.reduce((sum, v) => sum + v.effectiveValue, 0) * 1000000000) / 1000000000;
  const count = Math.round(values.reduce((sum, v) => sum + v.count, 0) * 1000000000) / 1000000000;
  const averageCount = values.reduce((sum, v) => sum + v.averageCount, 0);
  
  // シートの全パウチが時間かどうかを判定（GTP は参照先が全て時間なら含む）
  const allPouchesAreTime = sheetContributingPouchesAreAllTime(pouches, groups);
  
  let average;
  if (allPouchesAreTime) {
    // 全パウチが時間の場合、actualValueベースで平均を計算
    const actualValues = values.map(v => v.actualValue);
    average = Math.round((actualValues.reduce((sum, v) => sum + v, 0) / actualValues.length) * 1000000000) / 1000000000;
  } else {
    // 通常の場合、従来の計算方法
    average = Math.round((total / averageCount) * 1000000000) / 1000000000; // 浮動小数点精度問題を回避
  }

  let max, min;
  if (allPouchesAreTime) {
    // 全パウチが時間の場合、actualValueで比較
    const actualValues = values.map(v => v.actualValue);
    max = Math.max(...actualValues);
    min = Math.min(...actualValues);
  } else {
    // 通常の場合、掛け算モードとそれ以外で分けて最大・最小値を計算
    const multiplicationValues = values.filter(v => !v.isDivision).map(v => v.baseValue);
    const divisionValues = values.filter(v => v.isDivision).map(v => v.effectiveValue);

    if (multiplicationValues.length > 0) {
      // 掛け算モードのパウチが存在する場合、それらの中から最大・最小を取得
      max = Math.max(...multiplicationValues);
      min = Math.min(...multiplicationValues);
    } else {
      // 割り算モードのパウチのみの場合、実効値から最大・最小を取得
      max = Math.max(...divisionValues);
      min = Math.min(...divisionValues);
    }
  }

  return {
    count: Math.abs(count),  // カウントは常に正の値
    total: total,           // 合計は計算結果の符号をそのまま使用
    average: average,       // 平均は計算結果の符号をそのまま使用
    max: max,              // 最大値は計算結果の符号をそのまま使用
    min: min               // 最小値は計算結果の符号をそのまま使用
  };
};

/**
 * 色ごとのパウチの合計値を計算する
 * @param pouches パウチの配列
 * @param groups グループの配列
 * @returns 色ごとの合計値 { [colorId: string]: number }
 */
export const calculateColorTotals = (pouches: Pouch[], groups: Group[] = []): Record<string, number> => {
  const colorTotals: Record<string, number> = {};
  
  pouches.forEach(pouch => {
    // ミュートグループのパウチはスキップ
    if (isInMuteGroup(pouch.id, groups)) {
      return;
    }

    const colorId = pouch.color;
    const value = getPouchActualValue(pouch, groups);
    
    if (colorTotals[colorId] === undefined) {
      colorTotals[colorId] = value;
    } else {
      colorTotals[colorId] += value;
    }
  });
  
  return colorTotals;
};

/**
 * パウチのIDを指定して合計を計算する
 * @param pouchIds 計算対象のパウチID配列
 * @param pouches すべてのパウチの配列
 * @param groups グループの配列
 * @returns 合計値
 */
export const calculateTotalByIds = (pouchIds: string[], pouches: Pouch[], groups: Group[] = []): number => {
  return pouches
    .filter(p => pouchIds.includes(p.id) && !isInMuteGroup(p.id, groups))
    .reduce((total, pouch) => total + getPouchActualValue(pouch, groups), 0);
};

/**
 * 統計値を整形して表示用の文字列に変換する
 * @param value 統計値
 * @param decimalPlaces 小数点以下の桁数
 * @returns 整形された文字列
 */
export const formatStatisticValue = (value: number, decimalPlaces: number = 2): string => {
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }
  
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces
  });
};

// グループ内の合計値を計算する関数
export const calculateGroupSubtotal = (groupId: string, pouches: Pouch[], groups: Group[] = []) => {
  // グループ自体がミュートグループの場合は0を返す
  const group = groups.find(g => g.id === groupId);
  if (group && isMuteGroup(group)) {
    return 0;
  }

  // 親グループがミュートグループの場合も0を返す
  const isInMute = groups.some(g => 
    g.pouches?.includes(groupId) && isMuteGroup(g)
  );
  if (isInMute) {
    return 0;
  }

  // グループに所属するパウチのみをフィルタリング
  const groupPouches = pouches.filter(p => 
    group?.pouches?.includes(p.id) && 
    !p._isDeleting && 
    !isInMuteGroup(p.id, groups)
  );

  const result = groupPouches.reduce((total, pouch) => {
    const baseValue = pouch.value;
    const value = pouch.multiplier < 0 
      ? baseValue / Math.abs(pouch.multiplier)
      : baseValue * pouch.multiplier;
    return total + value;
  }, 0);
  return Math.round(result * 1000000000) / 1000000000;
}; 